/**
 * AcknowledgementModal - Standardized branded modal for user-meaningful messages
 * 
 * Use this modal ONLY for:
 * - Payment / Billing / Credits notifications
 * - Account & Access issues
 * - Data loss or irreversible outcomes
 * - Compliance / Legal acknowledgments
 * - Blocking system errors
 * 
 * DO NOT use for operational noise (saved, loaded, refreshing, etc.)
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, AlertTriangle, Info, CreditCard, User, ShieldAlert } from 'lucide-react';

// Severity levels determine icon and styling
const SEVERITY_CONFIG = {
  error: {
    icon: AlertCircle,
    iconColor: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
  },
  success: {
    icon: CheckCircle,
    iconColor: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
  },
  billing: {
    icon: CreditCard,
    iconColor: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
  },
  account: {
    icon: User,
    iconColor: 'text-indigo-500',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/20',
  },
  security: {
    icon: ShieldAlert,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-600/10',
    borderColor: 'border-red-600/20',
  },
};

const AcknowledgementModal = ({
  isOpen,
  onClose,
  title,
  message,
  severity = 'info',
  buttonText = 'OK',
  onButtonClick,
}) => {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
  const IconComponent = config.icon;

  const handleClose = () => {
    if (onButtonClick) {
      onButtonClick();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-slate-100">
        {/* NewAudia Logo Header */}
        <div className="flex justify-center pt-2 pb-4">
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-12 w-auto rounded-lg"
          />
        </div>

        {/* Icon and Title */}
        <DialogHeader className="text-center">
          <div className={`mx-auto w-12 h-12 rounded-full ${config.bgColor} ${config.borderColor} border flex items-center justify-center mb-3`}>
            <IconComponent className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          {title && (
            <DialogTitle className="text-lg font-semibold text-white text-center">
              {title}
            </DialogTitle>
          )}
        </DialogHeader>

        {/* Message Body */}
        <DialogDescription className="text-center text-slate-300 text-sm leading-relaxed px-4 py-2">
          {message}
        </DialogDescription>

        {/* OK Button */}
        <DialogFooter className="sm:justify-center pt-4">
          <Button
            onClick={handleClose}
            className="w-full sm:w-auto min-w-[120px] bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="acknowledgement-modal-ok"
          >
            {buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AcknowledgementModal;
